import React, { useState, useEffect, useRef } from"react";
import { useLocation, useNavigate } from"react-router-dom";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import ReactMarkdown from"react-markdown";
import remarkGfm from"remark-gfm";
import useSkillGenieSession from"../hooks/useSkillGenieSession";
import API_BASE_URL from"../Config/config";
import FeatureGuard from"./access/FeatureGuard";

const ExplainPage = ({ mode = "learner" }) => {
 const isTenantMode = mode === "tenant";

 // Tenant mode bypasses feature guard
 if (isTenantMode) {
   return <ExplainPageContent mode={mode} />;
 }

 // Learner mode requires TSG_AI access
 return (
   <FeatureGuard feature="TSG_AI">
     <ExplainPageContent mode={mode} />
   </FeatureGuard>
 );
};

const ExplainPageContent = ({ mode = "learner" }) => {
  const [input, setInput] = useState("");
  const [chat, setChat] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const chatEndRef = useRef(null);
  const navigate = useNavigate();

  const isTenantMode = mode === "tenant";

  const location = useLocation();
  const { session } = useSkillGenieSession();

  // Prefer language from navigation state, then session, then default 'en'
  const language = location.state?.language || session?.language || "en";
  const technology = location.state?.technology || "general";
  const topic = location.state?.topic || "general";

  const handleAsk = async () => {
    if (!input.trim()) return;

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const userId = user?.userId || user?.user_id || localStorage.getItem("user_id") || "UnknownUser";

    if (!userId || userId === "UnknownUser") {
      alert("Please log in to use this feature.");
      navigate("/login");
      return;
    }

    const currentInput = input;
    setChat((prev) => [...prev, { role: "user", content: currentInput }]);
    setInput("");
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE_URL}/tsg/explain_deep`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "user-id": userId
        },
        body: JSON.stringify({
          userId,
          prompt: currentInput,
          chatHistory: chat,
          language,
          technology,
          topic,
        }),
      });

      const data = await res.json();
      const reply = data.reply || "No reply.";
      setChat((prev) => [
        ...prev,
        { role: "assistant", content: reply },
      ]);
    } catch {
      setChat((prev) => [
        ...prev,
        { role: "assistant", content: "❌ Error reaching TSG." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const container = chatEndRef.current?.parentElement;
    if (container) {
      const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 100;
      if (isAtBottom || chat.length <= 2) {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [chat]);

  return (
    <div className="p-4 md:p-8 min-h-screen bg-[#F8FAFC] text-[#111827] font-sans">
      <div className="max-w-4xl mx-auto flex flex-col h-[calc(100vh-120px)] interactive-card p-0 shadow-[0_30px_60px_rgba(0,0,0,0.12)] border-none">
        {/* Decorative Background for Header */}
        <div className="absolute top-0 inset-x-0 h-40 bg-gradient-to-b from-purple-50/50 to-transparent pointer-events-none"></div>

        <div className="p-6 md:p-8 flex flex-col relative z-10">
          {/* Header Section */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10 border-b border-gray-50 pb-8">
            <div className="flex items-center gap-6">
              <button
                onClick={() => navigate(-1)}
                className="w-12 h-12 bg-white flex items-center justify-center rounded-2xl shadow-sm border border-gray-100 hover:bg-purple-50 hover:border-[#5B21B6] transition-all group"
                title="Go Back"
              >
                <ChevronLeftIcon className="w-6 h-6 text-[#5B21B6] group-hover:-translate-x-1 transition-transform" />
              </button>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-tr from-[#5B21B6] to-[#A855F7] rounded-2xl flex items-center justify-center shadow-lg shadow-purple-200 p-3">
                  <svg className="w-full h-full text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
                </div>
                <div>
                  <h1 className="text-3xl font-black tracking-tight text-[#111827]">Explain Deep</h1>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                    <span className="text-[10px] font-black uppercase tracking-widest text-[#5B21B6]">AI Assistant Online</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="px-4 py-2 bg-white border border-gray-100 rounded-xl shadow-sm flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#5B21B6]"></span>
                <span className="text-[10px] font-black uppercase tracking-widest text-gray-500">Mode:</span>
                <span className="text-[10px] font-black uppercase tracking-widest text-[#5B21B6]">{technology}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-purple-100 shadow-sm">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
            <span className="text-[10px] font-black text-gray-600 uppercase tracking-widest">lang: {language}</span>
          </div>
        </div>

        {/* Chat Messages Area */}
        <div className="flex-1 overflow-y-auto mb-6 pr-2 custom-scrollbar space-y-6">
          {chat.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center space-y-4 opacity-40">
              <div className="w-20 h-20 rounded-full bg-purple-50 flex items-center justify-center border border-purple-100">
                <svg className="w-10 h-10 text-[#5B21B6]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <div>
                <p className="font-bold uppercase tracking-widest text-sm text-[#111827]">How can I help you today?</p>
                <p className="text-xs font-bold text-gray-400 mt-1 uppercase tracking-tighter">Ask any complex topic and I'll explain it deep.</p>
              </div>
            </div>
          ) : (
            chat.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"} animate-in fade-in slide-in-from-bottom-2 duration-300`}
              >
                <div
                  className={`max-w-[85%] md:max-w-[75%] p-5 rounded-[24px] shadow-sm border-2 transition-all ${
                    msg.role === "user"
                      ? "bg-[#5B21B6] text-white rounded-tr-none border-[#5B21B6] shadow-purple-100"
                      : "bg-[#F9F7FF] text-[#111827] rounded-tl-none border-[#5B21B6] shadow-gray-50"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-[10px] font-black uppercase tracking-wider ${msg.role === "user" ? "text-purple-200" : "text-gray-400"}`}>
                      {msg.role === "user" ? "You" : "TSG Assistant"}
                    </span>
                  </div>
                  <div className={`prose max-w-none prose-p:leading-relaxed ${msg.role === 'user' ? 'prose-p:text-white prose-headings:text-white prose-strong:text-white prose-code:text-white' : 'prose-p:text-gray-600 prose-headings:text-[#5B21B6] prose-strong:text-[#5B21B6]'}`}>
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        p: ({ node, ...props }) => <p {...props} className="mb-0 font-medium" />,
                        code: ({ node, inline, ...props }) => (
                          <code
                            {...props}
                            className={`${
                              inline
                                ? (msg.role === 'user' ? "bg-white/20 text-white px-1.5 py-0.5 rounded text-sm font-bold" : "bg-purple-100 text-[#5B21B6] px-1.5 py-0.5 rounded text-sm font-bold")
                                : "block bg-[#1E1B4B] text-green-400 p-6 rounded-2xl border border-white/10 font-mono text-sm my-6 overflow-x-auto shadow-2xl"
                            }`}
                          />
                        ),
                      }}
                    >
                      {msg.content}
                    </ReactMarkdown>
                  </div>
                </div>
              </div>
            ))
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Sticky Input Area */}
        <div className="relative">
          {loading && (
            <div className="absolute -top-10 left-4 flex items-center gap-2 text-[#5B21B6] text-[10px] font-black uppercase tracking-widest">
              <span className="flex space-x-1">
                <span className="w-1.5 h-1.5 bg-[#5B21B6] rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                <span className="w-1.5 h-1.5 bg-[#5B21B6] rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                <span className="w-1.5 h-1.5 bg-[#5B21B6] rounded-full animate-bounce"></span>
              </span>
              TSG is thinking...
            </div>
          )}
          <div className="flex items-center gap-3 p-2 bg-white rounded-[28px] border border-purple-100 shadow-[0_20px_40px_rgba(91,33,182,0.08)] focus-within:ring-4 focus-within:ring-purple-50 transition-all">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleAsk()}
              placeholder="Ask me anything..."
              className="flex-1 bg-transparent px-6 py-4 text-sm font-bold text-[#111827] placeholder-gray-400 focus:outline-none"
            />
            <button
              onClick={handleAsk}
              disabled={loading || !input.trim()}
              className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-[#5B21B6] to-[#7C3AED] text-white flex items-center justify-center hover:scale-105 active:scale-95 transition-all disabled:opacity-30 disabled:hover:scale-100 shadow-xl shadow-purple-200"
            >
              <svg className={`w-6 h-6 transform ${loading ? 'animate-spin' : 'hover:translate-x-1 hover:-translate-y-1'} transition-transform`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
          <p className="mt-4 text-center text-[10px] font-black text-[#94A3B8] uppercase tracking-widest">
             AI-POWERED BY THE SKILLGEN ELITE MASTERY SYSTEM
          </p>
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="w-full max-w-md bg-gradient-to-br from-[#1a1a2e] to-[#16213e] border border-primary-purple/40 rounded-3xl shadow-2xl p-8 text-center animate-in zoom-in-95 duration-300">
            <div className="w-20 h-20 bg-primary-purple/20 rounded-full flex items-center justify-center mx-auto mb-6 border border-primary-purple/30">
              <span className="text-4xl">💎</span>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Premium Feature</h2>
            <p className="text-gray-400 mb-8 leading-relaxed">
              Unlock the full potential of TSG Deep Explanation with our premium learning tracks.
            </p>
            <div className="space-y-4">
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  navigate("/subscription-plans");
                }}
                className="w-full bg-primary-purple text-white font-bold py-4 rounded-xl hover:shadow-[0_0_20px_rgba(91,33,182,0.4)] transition-all transform hover:-translate-y-0.5"
              >
                View Plans →
              </button>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="w-full text-gray-500 hover:text-white transition-colors text-sm font-medium"
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExplainPage;



